import styled from "styled-components";
import { Button, Typography, TypographyVariant } from "../ui-kit";
import { FenaLogo } from "../icons";
import { AppRoutes } from "../../constants/routes";
import { useLocation, useNavigate } from "react-router-dom";

const BuySubscription = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: #76767661;
  z-index: 2;
`;

const Span = styled.span`
  font-weight: 700;
`;

const BuyModal = styled.div`
  padding: 30px 50px;
  border-radius: 10px;
  background-color: #fff;
  width: 450px;
`;

const LogoAndName = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
`;

const StyledTypography = styled(Typography)`
  margin-top: 20px;
`;

const Description = styled(Typography)`
  margin-top: 10px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const PurchaseButton = styled(Button)`
  margin-top: 20px;
`;

const getTabInfo = (tabName: string) => {
  switch (tabName) {
    case AppRoutes.Private.Inventory.ORDERS_LIST:
      return {
        title: "Orders",
        description:
          "Maximise control over your sales with our Orders Tab. Easily track and filter all orders from web and mobile sources by timeframe, price, source, status, and payment type. This powerful tool allows you to oversee every order detail in one place, ensuring efficient management and seamless operation.",
      };
    case AppRoutes.Private.Report.CARD_PAYMENTS:
      return {
        title: "Reports",
      };
    case AppRoutes.Private.Inventory.PRODUCT_LIST:
      return {
        title: "Products list",
        description:
          "Our comprehensive Product Information Management allows you to list and update your product offerings across the company effortlessly. Import and export product data via CSV, add new products manually, manage variants, and sync selected products. Streamline your inventory processes and keep your sales details correct and up-to-date.",
      };
    case AppRoutes.Private.Inventory.PRODUCT_LIST_STOCK:
      return {
        title: "Stock",
        description:
          "Stay informed about your stock levels with our intuitive Stock Management tab. Monitor inventory levels, and get immediate updates on low or out-of-stock items. This feature helps you maintain optimal stock levels, ensuring you never miss a sale due to inventory shortages.",
      };
    case AppRoutes.Private.Inventory.CATEGORIES:
      return {
        title: "Categories",
        description:
          "Efficiently manage product categories and sub-categories in our Categories Tab. Create new categories, track and analyse the number of products assigned to each. This organisation tool enhances product management and simplifies navigation and reporting.",
      };
    case AppRoutes.Private.Inventory.BRANDS:
      return {
        title: "Brands",
        description:
          "In the Brands Tab, oversee and expand your product brands. Create new brands and track the alignment of products to specific brands, facilitating better marketing strategies and brand recognition management.",
      };
    case AppRoutes.Private.Inventory.INCOMING_INVENTORY:
      return {
        title: "Incoming inventory",
        description:
          "Track incoming stock with precision. Our Incoming Inventory Tab lets you record and monitor orders from arrival to storage, adding new transfers and purchase orders. Stay on top of your inventory supply chain and optimise handling and storage.",
      };
    case AppRoutes.Private.Inventory.REJECTED_AND_MISSING:
      return {
        title: "Rejected and missing",
        description:
          "Address inventory discrepancies with our Rejected and Missing Tab. Track and manage any stock discrepancies, including missing, rejected, or damaged items, ensuring accuracy and accountability in your inventory management.",
      };
    case AppRoutes.Private.Inventory.ORDER_GENERATION_EPOS:
      return {
        title: "Order generation",
        description:
          "Streamline order creation with our Order Generation feature. Whether you sell retail or wholesale, in-person or for delivery/pickup, you can generate orders easily with Fena payment links. Tailor each order to meet specific needs, enhancing customer satisfaction and operational efficiency.",
      };
    case AppRoutes.Private.Inventory.ORDER_GENERATION_NEW_SALES_ORDER:
      return {
        title: "Order generation",
        description:
          "Streamline order creation with our Order Generation feature. Whether you sell retail or wholesale, in-person or for delivery/pickup, you can generate orders easily with Fena payment links. Tailor each order to meet specific needs, enhancing customer satisfaction and operational efficiency.",
      };
    case AppRoutes.Private.Suppliers.SUPPLIERS:
      return {
        title: "Suppliers",
        description:
          "Manage your suppliers effortlessly. Our Suppliers Tab allows you to maintain a comprehensive database, create new supplier entries, and access supplier information quickly, making supplier management simpler and more effective.",
      };
    case AppRoutes.Private.Payments.BULK_PAYMENTS_RUNS:
    case AppRoutes.Private.Payments.BULK_PAYMENTS_UPCOMING:
      return {
        title: "Bulk payments",
      };
    case AppRoutes.Private.Customers.CUSTOMERS_LIST:
      return {
        title: "Customers",
        description:
          "Enhance your customer relations with our Customers Tab. Easily manage customer information, track interactions, and access data across the app. This tool is designed to help you maintain a detailed customer database, improving service and engagement. Keep everything you need to know about your customers at your fingertips, allowing for personalised interactions and better business decisions.",
      };
    case AppRoutes.Private.Payments.TRANSACTIONS_LIST:
      return {
        title: "Transactions",
        description:
          "Gain a clear overview of your financial transactions. View and manage both incoming and outgoing transactions, with the option to download data directly from the app. This tab provides financial clarity and aids in precise bookkeeping.",
      };
    case AppRoutes.Private.Payments.FENA_PAYMENTS_LIST:
    case AppRoutes.Private.Payments.FENA_PAYMENTS_QR:
    case AppRoutes.Private.Payments.FENA_PAYMENTS_MOBILE:
    case AppRoutes.Private.Payments.PAYMENTS_CASH:
    case AppRoutes.Private.Payments.PAYMENTS_OTHER:
    case AppRoutes.Private.Payments.RECURRING_PAYMENTS:
      return {
        title: "Payments",
        description:
          "Our Payments Tab simplifies financial transactions across different payment types. Thanks to this you can manage open banking, cash, and card payments generated across the app. Keep track of all transactions efficiently, ensuring financial fluidity and security.",
      };
    case AppRoutes.Private.Invoices.INVOICES_LIST:
    case AppRoutes.Private.Invoices.INVOICES_DETAILS:
    case AppRoutes.Private.Invoices.INVOICES_CREATE_RETAIL:
    case AppRoutes.Private.Invoices.INVOICES_CREATE_WHOLESALE:
    case AppRoutes.Private.Invoices.INVOICES_EDIT_RETAIL:
    case AppRoutes.Private.Invoices.INVOICES_EDIT_WHOLESALE:
      return {
        title: "Invoices",
        description:
          "In the Invoices Tab, manage and generate retail and wholesale invoices with ease. Create, customise, and track invoices to ensure timely payments and maintain financial consistency.",
      };
    case AppRoutes.Private.Invoices.QUOTES_LIST:
    case AppRoutes.Private.Invoices.QUOTES_CREATE_RETAIL:
    case AppRoutes.Private.Invoices.QUOTES_CREATE_WHOLESALE:
    case AppRoutes.Private.Invoices.QUOTES_EDIT_RETAIL:
    case AppRoutes.Private.Invoices.QUOTES_EDIT_WHOLESALE:
    case AppRoutes.Private.Invoices.QUOTES_DETAILS:
      return {
        title: "Quotes",
        description:
          "In the Quotes Tab, manage and generate retail and wholesale quotes with ease.",
      };
    case AppRoutes.Private.Shipments.SHIPMENTS_PICKER_LIST:
    case AppRoutes.Private.Shipments.SHIPMENTS_ORDER_DETAILS:
    case AppRoutes.Private.Shipments.SHIPMENTS_PICKING_LIST:
    case AppRoutes.Private.Shipments.SHIPMENTS_PICKER_DETAILS:
    case AppRoutes.Private.Shipments.SHIPMENTS_DESPATCH_LIST:
    case AppRoutes.Private.Shipments.SHIPMENTS_DESPATCH_DETAILS:
    case AppRoutes.Private.Shipments.SHIPMENTS_MANIFEST_HISTORY:
      return {
        title: "Shipments",
        description:
          "Optimise your shipping process with our Shipments Tab. Manage picking lists, picker assignments, dispatch lists, and manifest histories. This tool helps streamline your shipping operations, reducing errors and improving delivery efficiency.",
      };
    case AppRoutes.Private.Settings.BANK_ACCOUNTS:
      return {
        title: "Bank accounts",
        description:
          "Ensure secure transactions with our Bank Accounts Tab. Add and verify bank accounts to streamline payment processes, enabling direct receipt of all payments to designated accounts. This feature promotes financial security and efficiency, crucial for smooth business operations.",
      };
    case AppRoutes.Private.Settings.ROLES:
      return {
        title: "Roles management",
        description:
          "Customise team capabilities with our Roles Management. Start quickly with smart predefined roles or create custom roles to define access levels. This easy-to-use management tool is ideal for scaling your business and bringing new team members onboard efficiently.",
      };
    case AppRoutes.Private.Settings.TEAM_MANAGEMENT:
      return {
        title: "Team management",
        description:
          "Empower your team with our Team Management Tab. Add users, assign roles, and manage access levels across your app. This feature allows for precise control over who can manage and access critical business functions, enhancing teamwork and operational integrity.",
      };
    case AppRoutes.Private.Settings.API_KEYS:
      return {
        title: "API keys",
        description:
          "Facilitate seamless integrations with our API Keys section. Generate and manage API keys essential for integrating your systems with our robust API. This feature supports secure and efficient connectivity with other services and tools, enhancing your app’s functionality.",
      };
    case AppRoutes.Private.Settings.PICK_UP_LOCATIONS:
      return {
        title: "Pick-up locations",
        description:
          "Optimise order pickup with our Pick Up Locations Tab. Manage and define pickup points, enhancing convenience for your customers and streamlining order collection.",
      };
    case AppRoutes.Private.Settings.INTEGRATIONS_APPS:
    case AppRoutes.Private.Settings.INTEGRATIONS_APPS_LIST:
    case AppRoutes.Private.Settings.INTEGRATIONS_APPS_DETAILS:
      return {
        title: "Apps",
        description:
          "The Apps tab allows you to easily manage connected B2B apps.",
      };
    case AppRoutes.Private.Settings.CURRENCY:
      return {
        title: "Currency",
        description:
          "The Currency tab allows you to manage the currencies in your account.",
      };
    case AppRoutes.Private.Settings.INTEGRATIONS_ACCOUNTING:
      return {
        title: "Accounting integrations",
        description:
          "Simplify your accounting processes with integrations for platforms like Xero and QuickBooks. Our accounting integrations tab allows you to connect and manage your accounting software directly through the app, ensuring financial consistency and visibility.",
      };
    case AppRoutes.Private.Settings.INTEGRATIONS_OTHERS:
      return {
        title: "Other Apps",
        description:
          "Expand your business capabilities with our Other Integrations. Connect and manage various tools and services to enhance your business operations and streamline your workflow.",
      };
    case AppRoutes.Private.Settings.INTEGRATIONS_ECOMMERCE:
      return {
        title: "eCommerce stores integrations",
        description:
          "Expand your online presence with our E-commerce Stores integration. Connect and manage your accounts with platforms like WooCommerce, Shopify, and more, enhancing your sales channels and market reach as well as allowing the synchronisation between multiple stores.",
      };
    case AppRoutes.Private.Settings.INTEGRATIONS_MARKETPLACES:
      return {
        title: "Marketplaces integrations",
        description:
          "Boost your market visibility with our Marketplaces integration. Manage connections with major platforms like Amazon and Faire, opening up new sales opportunities and expanding your customer base.",
      };
    case AppRoutes.Private.Settings.INTEGRATIONS_CARRIERS:
      return {
        title: "Couriers integrations",
        description:
          "Streamline your shipping processes with our Couriers integration. Manage relationships with major couriers like Royal Mail, DPD, DHL, and others to enhance your delivery capabilities and customer satisfaction.",
      };
    case AppRoutes.Private.Settings.CUSTOMISATION_PAYMENT_PAGE:
    case AppRoutes.Private.Settings.CUSTOMISATION_EMAIL_NOTIFICATIONS:
    case AppRoutes.Private.Settings.CUSTOMISATION_INVOICE_DESIGN:
      return {
        title: "Customisation",
        description:
          "Tailor the app’s appearance with our Customization Tab. Customise emails, payment pages, and invoices to reflect your brand’s style and values. Define colours, text content, and logos to enhance your brand’s consistency and recognition.",
      };
    case AppRoutes.Private.Settings.LOCATIONS_LIST:
      return {
        title: "Locations",
        description:
          "Manage your business locations effectively with our Locations Tab. Define and manage various locations for order generation, both for online and in-person transactions, ensuring operational flexibility and efficiency.",
      };
    case AppRoutes.Private.Settings.REMINDERS_LIST:
      return {
        title: "Reminders",
        description:
          "Keep your organisation on track with our Reminders Tab. Create custom notifications for invoices, set to trigger on due or overdue dates. Customise email subjects and messages to keep your customers informed and engaged, ensuring timely payments and financial health.",
      };
    case AppRoutes.Private.Settings.TERMINAL_MANAGEMENT_LIST:
      return {
        title: "Reminders",
        description:
          "Control payment terminals efficiently with our Terminal Management section. Create and manage terminals, assign them to specific bank accounts, and define access permissions for users, ensuring secure and organised payment processing.",
      };
    case AppRoutes.Private.Settings.COMMERCIAL_INVOICES:
    case AppRoutes.Private.Invoices.COMMERCIAL_INVOICES_LIST:
    case AppRoutes.Private.Invoices.COMMERCIAL_INVOICES_DETAILS:
    case AppRoutes.Private.Invoices.COMMERCIAL_INVOICES_EDIT:
      return {
        title: "Commercial Invoices",
        description:
          "You can manage the setup of commercial invoice generation.",
      };
    default:
      return {
        title: undefined,
        description: "Description",
      };
  }
};

interface DisableContentProps {
  userName?: string;
  hasPaymentsOnlySubscription: boolean;
  hasB2BSubscription: boolean;
}

const DisableContent: React.FunctionComponent<DisableContentProps> = ({
  userName,
  hasPaymentsOnlySubscription,
  hasB2BSubscription,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const handlePurchaseSub = () => {
    navigate(AppRoutes.Private.Settings.BILLING_CHOOSE_PLAN);
  };

  const currentPath = location.pathname;

  const { title, description } = getTabInfo(currentPath);

  return (
    <BuySubscription>
      <BuyModal>
        <LogoAndName>
          <FenaLogo />
          <StyledTypography variant={TypographyVariant.HEADER2}>
            Welcome {userName}!
          </StyledTypography>
        </LogoAndName>
        {hasPaymentsOnlySubscription || hasB2BSubscription ? (
          <>
            <StyledTypography variant={TypographyVariant.HEADER2}>
              Your current subscription does not include {title ?? "this"}
            </StyledTypography>
          </>
        ) : (
          <>
            <StyledTypography variant={TypographyVariant.BODY5}>
              Currently, you do not have any active subscription. To purchase a
              subscription, please follow below link.{" "}
            </StyledTypography>
            <StyledTypography variant={TypographyVariant.BODY5}>
              After selecting and purchasing your subscription, you will gain
              access to all features available for the selected subscription
              tier.
            </StyledTypography>
          </>
        )}

        {title && (
          <>
            {description && (
              <StyledTypography variant={TypographyVariant.BODY5}>
                <Span>What can you do in {title} tab?</Span>
              </StyledTypography>
            )}

            {description && (
              <Description variant={TypographyVariant.BODY3}>
                {description}
              </Description>
            )}
          </>
        )}

        <ButtonWrapper>
          <PurchaseButton onClick={handlePurchaseSub}>
            {hasPaymentsOnlySubscription || hasB2BSubscription
              ? "Upgrade"
              : "Start your free trial"}
          </PurchaseButton>
        </ButtonWrapper>
      </BuyModal>
    </BuySubscription>
  );
};

export default DisableContent;
